import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { term: "", definition: "" };

export default function AdminLexique() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/lexique").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const openEdit = (e) => {
    setEditing(e.id);
    setForm({ term: e.term, definition: e.definition });
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/lexique", form);
        toast.success("Terme ajouté.");
      } else {
        await api.put(`/admin/lexique/${editing}`, form);
        toast.success("Terme mis à jour.");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce terme ?")) return;
    try {
      await api.delete(`/admin/lexique/${id}`);
      toast.success("Terme supprimé.");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-lexique">
      <div className="flex items-center justify-between mb-10">
        <div>
          <span className="font-overline text-[#B8935A]">Back-office</span>
          <h1 className="font-serif text-3xl text-[#0F172A] mt-2">Lexique</h1>
        </div>
        <button onClick={openNew} data-testid="admin-lex-new" className="gpi-btn-primary">
          <Plus size={16} /> Nouveau terme
        </button>
      </div>

      <div className="bg-white border border-[#E2E8F0]">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-left text-[#64748B] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 w-1/4">Terme</th>
              <th className="px-5 py-3">Définition</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-[#E2E8F0]" data-testid={`admin-lex-row-${e.id}`}>
                <td className="px-5 py-4 font-medium text-[#0F172A]">{e.term}</td>
                <td className="px-5 py-4 text-[#475569] line-clamp-2">{e.definition}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEdit(e)}
                    data-testid={`admin-lex-edit-${e.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#0F172A] hover:bg-[#EFE8DC] mr-1"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(e.id)}
                    data-testid={`admin-lex-delete-${e.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[#991B1B] hover:bg-[#FEE2E2]"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-12 text-center text-[#64748B]">
                  Aucun terme.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start lg:items-center justify-center p-4 overflow-auto">
          <form onSubmit={save} className="bg-white w-full max-w-2xl border border-[#E2E8F0] my-8" data-testid="admin-lex-form">
            <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]">
              <h2 className="font-serif text-xl text-[#0F172A]">
                {editing === "new" ? "Nouveau terme" : "Modifier le terme"}
              </h2>
              <button type="button" onClick={close} className="text-[#64748B] hover:text-[#0F172A]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <label className="block">
                <span className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">
                  Terme <span className="text-[#B8935A]">*</span>
                </span>
                <input
                  data-testid="lex-form-term"
                  value={form.term}
                  onChange={(ev) => setForm({ ...form, term: ev.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-[#F9F8F6] focus:border-[#0F172A] focus:bg-white outline-none text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">
                  Définition <span className="text-[#B8935A]">*</span>
                </span>
                <textarea
                  data-testid="lex-form-definition"
                  rows={6}
                  value={form.definition}
                  onChange={(ev) => setForm({ ...form, definition: ev.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-[#E2E8F0] bg-[#F9F8F6] focus:border-[#0F172A] focus:bg-white outline-none text-sm"
                />
              </label>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-[#E2E8F0] bg-[#F9F8F6]">
              <button type="button" onClick={close} className="gpi-btn-outline">
                Annuler
              </button>
              <button type="submit" className="gpi-btn-primary" data-testid="admin-lex-save">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
